import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useOrgData } from '../hooks/useOrgData';
import { isAdmin } from '../lib/rbac';
import { setStageAreas } from '../lib/data';
import type { Phase } from '../shared/types';

const PHASES: Phase[] = ['PD1', 'PD2', 'PD3', 'PD4', 'PD5'];

/** Checklist: quais áreas são responsáveis (podem editar) cada etapa. Org-wide. */
export function Etapas() {
  const { currentOrgId, grants, areas } = useAuth();
  const { loading, template, refresh } = useOrgData(currentOrgId);
  const areaIdByKey = useMemo(
    () => Object.fromEntries(areas.map((a) => [a.key, a.id])),
    [areas],
  );
  const [sel, setSel] = useState<Record<number, string[]>>({});
  const [busyN, setBusyN] = useState<number | null>(null);

  useEffect(() => {
    const m: Record<number, string[]> = {};
    for (const t of template) m[t.n] = t.areaKeys;
    setSel(m);
  }, [template]);

  if (!grants || !isAdmin(grants)) {
    return (
      <>
        <div className="topbar"><h1>Etapas & responsáveis</h1></div>
        <div className="content">
          <div className="card card-pad">
            <p className="muted">Apenas administradores configuram as etapas.</p>
          </div>
        </div>
      </>
    );
  }

  async function toggle(n: number, key: string) {
    const cur = sel[n] ?? [];
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    setSel((s) => ({ ...s, [n]: next }));
    setBusyN(n);
    const ids = next.map((k) => areaIdByKey[k]).filter(Boolean) as string[];
    const { error } = await setStageAreas(currentOrgId!, n, ids);
    setBusyN(null);
    if (error) {
      alert(`Não foi possível salvar: ${error}`);
      refresh();
    }
  }

  const ordered = [...template].sort((a, b) => a.n - b.n);

  return (
    <>
      <div className="topbar">
        <h1>Etapas &amp; responsáveis</h1>
      </div>
      <div className="content">
        <div className="card card-pad">
          <p className="muted" style={{ fontSize: 12.5, marginTop: 0 }}>
            Marque, para cada etapa, <b>quais áreas podem editá-la</b>. Vale para todos os projetos.
            Uma pessoa é apta a uma etapa quando pertence a alguma das áreas marcadas (admin/gestão
            editam tudo). Ex.: se uma etapa é só de Marketing e você quer que o Comercial também
            possa, marque também <b>Comercial</b> aqui.
          </p>
          {loading ? (
            <div className="center-note">Carregando…</div>
          ) : (
            PHASES.map((ph) => {
              const stages = ordered.filter((t) => t.phase === ph);
              if (!stages.length) return null;
              return (
                <div key={ph} style={{ marginBottom: 18 }}>
                  <div className="phase-head" style={{ borderRadius: 8, marginBottom: 6 }}>
                    <span className="tag">{ph}</span>
                  </div>
                  {stages.map((t) => (
                    <div className="etapa-row" key={t.n}>
                      <div className="etapa-name">
                        <span className="muted tabular">{t.n}.</span> {t.name}
                      </div>
                      <div className="chip-toggle" style={{ flexWrap: 'wrap' }}>
                        {areas.map((a) => (
                          <button
                            key={a.key}
                            type="button"
                            className={(sel[t.n] ?? []).includes(a.key) ? 'on' : ''}
                            disabled={busyN === t.n}
                            onClick={() => void toggle(t.n, a.key)}
                          >
                            {a.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
